import React, { useState } from 'react';
import useDealBuilderResultStore from '../../stores/deal-builder-result.store.ts';
import { DealBuilder } from '../services/deal-builder.ts';
import { Button, Divider, IconButton, Menu, MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Add, Lock, LockOpen, Tune } from '@mui/icons-material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SerialBondRow from './bonds-page/serial-bond-row.tsx';
import moment from 'moment';
import InterestRow from './bonds-page/interest-row.tsx';
import formatter from '../../tools/formatter.js';
import useButtonSwitching from '../hooks/useButtonSwitching.ts';
import RedemptionRow from './bonds-page/redemption-row.tsx';
import NewTicDisplay from './shared/new-tic-display.tsx';

export interface SerialBond {
  type: "serial",
  maturity: string;
  principal: number;
  coupon: number;
  lockedCoupon: boolean,
  lockedPrincipal: boolean,
}

export interface Redemption {
  date: string,
  amount: number,
  redemptionPrice: number,
  locked: boolean
}

export interface TermBond {
  type: "term",
  maturity: string;
  coupon: number;
  lockedCoupon: boolean,
  redemptions: Redemption[];
}

export type Bond = SerialBond | TermBond;

interface BondsPageProps {
  builder: DealBuilder
}

export default function BondsPage({ builder }: BondsPageProps) {
  const { setDealBuilderService, DealBuilderService } = useDealBuilderResultStore();
  const showCapitalizedInterest = builder.debtService.some(x => x.capitalizedInterest !== 0);
  const [dateToTargetMap, redemptionToBondMap] = generateTargetsMaps();
  const [principalButtonAnchor, setPrincipalButtonAnchor] = useState<null | HTMLElement>(null);
  useButtonSwitching();

  async function onLevelDebt() {
    setPrincipalButtonAnchor(null);
    builder.updateDebtService(true);
    await builder.updateProduction();
    setDealBuilderService(builder);
    await builder.updateProduction(builder.bonds);
    setDealBuilderService(builder);
  }

  function lockAllPrincipals() {
    setPrincipalButtonAnchor(null);
    builder.bonds.forEach(bond => {
      if (bond.type === "serial") bond.lockedPrincipal = true;
      else bond.redemptions.forEach(r => { r.locked = true; });
    });
    setDealBuilderService(builder);
  }

  function unlockAllPrincipals() {
    setPrincipalButtonAnchor(null);
    builder.bonds.forEach(bond => {
      if (bond.type === "serial") bond.lockedPrincipal = false;
      else bond.redemptions.forEach(r => { r.locked = false; });
    });
    setDealBuilderService(builder);
  }

  function generateTargetsMaps(): [Map<string, (SerialBond | Redemption)[]>, Map<Redemption, TermBond>]{
    const dateToTargetMap: Map<string, (SerialBond | Redemption)[]> = new Map();
    const redemptionToBondMap: Map<Redemption, TermBond> = new Map();
    for (const bond of builder.bonds) {
        if (bond.type === "serial") {
          const dateStr = moment(bond.maturity).startOf("day").toISOString();
          const current = dateToTargetMap.get(dateStr) || [];
          current.push(bond);
          dateToTargetMap.set(dateStr, current);
        } else if (bond.type === "term") {
          for (const redemption of bond.redemptions) {
            const dateStr = moment(redemption.date).startOf("day").toISOString();
            const current = dateToTargetMap.get(dateStr) || [];
            current.push(redemption);
            dateToTargetMap.set(dateStr, current);
            redemptionToBondMap.set(redemption, bond);
          }
        }
    }
    return [dateToTargetMap, redemptionToBondMap];
  }

  return (
    <Stack direction="column" gap={2} width="100%" margin={2}>

      <TableContainer
        sx={theme => ({
          borderRadius: 1,
          boxShadow: theme.shadows[1],
          border: '1px solid #f4f4f4',
          backgroundColor: 'white',
          transition: 'all 0.2s ease',
          borderBottom: builder.bonds.length === 0 ? '1px solid #f4f4f4' : 0,
        })}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell colSpan={99}>
                <Stack direction="row" justifyContent="space-between" py={1} gap={2} width="100%">
                  <Stack direction="row" gap={2}>
                    {DealBuilderService && (
                      <NewTicDisplay builder={DealBuilderService} />
                    )}

                    <Button
                      size="small"
                      startIcon={<Add />}
                      sx={{ color: 'primary.main', '&:hover': { color: 'primary.dark' } }}
                      onClick={async () => {
                        builder.addSerialBond();
                        builder.updateDebtService();
                        setDealBuilderService(builder);
                        await builder.updateProduction(builder.bonds);
                        setDealBuilderService(builder);
                      }}
                    >
                      Add Bond
                    </Button>
                  </Stack>
                </Stack>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ py: "12px" }}>Maturity</TableCell>
              <TableCell align="right">
                <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={0.5}>
                  Principal
                  <IconButton size="small" onClick={e => setPrincipalButtonAnchor(e.currentTarget)} sx={{ p: 0.25 }}>
                    <MoreVertIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <Menu
                    anchorEl={principalButtonAnchor}
                    open={principalButtonAnchor !== null}
                    onClose={() => setPrincipalButtonAnchor(null)}
                  >
                    <MenuItem sx={{ p: 0 }}>
                      <Button fullWidth sx={{ px: 3, py: 1, justifyContent: 'flex-start' }} startIcon={<Tune fontSize="small" />} onClick={onLevelDebt}>
                        Level debt
                      </Button>
                    </MenuItem>
                    <Divider />
                    <MenuItem sx={{ p: 0 }}>
                      <Button fullWidth sx={{ px: 3, py: 1, justifyContent: 'flex-start' }} startIcon={<Lock fontSize="small" />} onClick={lockAllPrincipals}>
                        Lock all
                      </Button>
                    </MenuItem>
                    <MenuItem sx={{ p: 0 }}>
                      <Button fullWidth sx={{ px: 3, py: 1, justifyContent: 'flex-start' }} startIcon={<LockOpen fontSize="small" />} onClick={unlockAllPrincipals}>
                        Unlock all
                      </Button>
                    </MenuItem>
                  </Menu>
                </Stack>
              </TableCell>
              <TableCell align="right">Coupon</TableCell>
              <TableCell align="right">Acc. Interest</TableCell>
              
              {showCapitalizedInterest && (
                <TableCell align="right">Cap. Interest</TableCell>
              )}

              <TableCell align="right">Debt Service</TableCell>
              <TableCell align="right">Annual DS</TableCell>
              <TableCell align="center" sx={{ width: '48px' }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {builder.bonds.length === 0 ? (
              <TableRow>
                <TableCell colSpan={99} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  You don't have configured bonds.
                </TableCell>
              </TableRow>
            ) : (
              builder.debtService.map((row, rowIndex) => {
                const dateStr = moment(row.date).startOf("day").toISOString();
                const targets = dateToTargetMap.get(dateStr);
                
                if (!targets) {
                  return (
                    <InterestRow 
                      key={row.date}
                      date={moment(row.date)} 
                      debtServiceRow={row}
                      showCapitalizedInterest={showCapitalizedInterest}
                    />
                  )
                }


                return targets.map((target, targetIndex) => {
                  const isSerial = "type" in target;

                  return (
                    <React.Fragment key={`${rowIndex}-${targetIndex}`}>
                      {isSerial ? (
                        <SerialBondRow 
                          builder={builder} 
                          bond={target} 
                          debtServiceRow={targetIndex === 0 ? row : undefined} 
                          showCapitalizedInterest={showCapitalizedInterest}
                          border={targetIndex === targets.length - 1}
                        />
                      ) : (
                        <RedemptionRow 
                          builder={builder} 
                          bond={redemptionToBondMap.get(target)!} 
                          debtServiceRow={targetIndex === 0 ? row : undefined} 
                          showCapitalizedInterest={showCapitalizedInterest}
                          redemption={target}
                          border={targetIndex === targets.length - 1}
                        />
                      )}
                    </React.Fragment>
                  )

                });
              })

            )}

            <TableRow>
              <TableCell sx={{ py: "12px", fontWeight: 600 }}>Total</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>{formatter.money(builder.getTotalPrincipal())}</TableCell>
              <TableCell align="right"></TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>{formatter.money(builder.getTotalInterest())}</TableCell>
              
              {showCapitalizedInterest && (
                <TableCell align="right">{formatter.money(builder.getTotalCapitalizedInterest())}</TableCell>
              )}

              <TableCell align="right" sx={{ fontWeight: 600 }}>{formatter.money(builder.getTotalDebtService())}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>{formatter.money(builder.getTotalAnnualDebtService())}</TableCell>
              <TableCell align="center" sx={{ width: '48px' }}></TableCell>
            </TableRow>

          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};
